import React, { useEffect, useState } from "react";
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { X } from 'lucide-react';
import { SEOHead } from './SEOHead';
import { PageFaq } from './PageAeo';
import { useSiteSettings, getPageSeo, buildFaqSchema, safeUrl, TeamMember } from '../services/SiteSettings';
import { SITE_URL } from '../data/organizationSchema.mjs';
import { getPublicDocument, getDocumentDownloadUrl, EXPERT_ROSTER_KEY } from '../services/Document';

const absoluteUrl = (src: string) => (src.startsWith('/') ? `${SITE_URL}${src}` : src);

export function WhoWeArePage() {
  const settings = useSiteSettings();
  const { about, contact } = settings;
  const members = settings.team.members || [];
  const seo = getPageSeo(settings, '/who-we-are');

  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [rosterUrl, setRosterUrl] = useState('/Roster of Additional Experts and Field Researchers.pdf');

  useEffect(() => {
    getPublicDocument(EXPERT_ROSTER_KEY)
      .then((doc) => {
        if (doc?.fileName) setRosterUrl(getDocumentDownloadUrl(doc.fileName));
      })
      .catch(() => {
        // Keep bundled PDF as fallback
      });
  }, []);

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
      { "@type": "ListItem", "position": 2, "name": "Who We Are", "item": `${SITE_URL}/who-we-are` }
    ]
  };

  const teamSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "USP Consultancy Team",
    "itemListElement": members.map((m, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "item": {
        "@type": "Person",
        "name": m.name,
        "jobTitle": m.role,
        ...(safeUrl(m.image) ? { "image": absoluteUrl(safeUrl(m.image)) } : {}),
        "worksFor": { "@type": "Organization", "name": contact.name, "url": `${SITE_URL}/` }
      }
    }))
  };

  const faqSchema = buildFaqSchema(seo.faqs);
  const structuredData = [breadcrumbSchema, teamSchema, ...(faqSchema ? [faqSchema] : [])];

  return (
    <div className="min-h-screen py-16">
      <SEOHead
        title={seo.title || 'Who We Are'}
        description={seo.description}
        canonical={`${SITE_URL}/who-we-are`}
        keywords={seo.keywords}
        structuredData={structuredData}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <header className="text-center mb-16">
          <h1 className="mb-6">Who We Are</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">{about.summary}</p>
        </header>

        <section className="mb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-1 lg:order-2">
              <ImageWithFallback
                src={safeUrl(about.storyImage) || '/hero/1.jpg'}
                alt={`${contact.name} team at work`}
                className="w-full h-80 object-cover rounded-lg shadow-lg"
              />
            </div>
            <div className="order-2 lg:order-1">
              <h2 className="mb-6">Our Story</h2>
              <div className="space-y-4 text-gray-600">
                {about.story.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <div className="text-center mb-12">
            <h2 className="mb-4">Our Team</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Meet our dedicated professionals who bring diverse expertise and passion to every project.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {members.map((member, index) => (
              <Card
                key={index}
                className="text-center cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-105 border-2 border-transparent hover:border-blue-200 flex flex-col h-full"
                onClick={() => setSelectedMember(member)}
              >
                <CardContent className="pt-6 pb-4 flex flex-col flex-grow">
                  <div className="mb-4 flex justify-center">
                    <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-blue-100 bg-gray-200 flex items-center justify-center">
                      <ImageWithFallback
                        src={safeUrl(member.image)}
                        alt={member.name}
                        className="w-full h-full object-cover"
                        fallbackSrc="/team/placeholder.jpg"
                      />
                    </div>
                  </div>
                  <div className="flex-grow">
                    <h3 className="mb-2 text-sm font-semibold leading-tight line-clamp-2">{member.name}</h3>
                    <p className="text-gray-600 text-xs mb-3 line-clamp-2">{member.role}</p>
                  </div>
                  <Button variant="link" className="text-blue-600 hover:text-blue-800 text-xs p-0 h-auto cursor-pointer mt-auto">
                    View Profile →
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <div className="my-8 mb-12">
          <h2 className="text-center mb-4 text-xl font-semibold">Roster of Additional Experts and Field Researchers</h2>
          <div className="flex justify-center">
            <Button asChild>
              <a href={rosterUrl} download="Roster of Additional Experts and Field Researchers.pdf">
                Download Roster PDF
              </a>
            </Button>
          </div>
        </div>

        <section className="bg-gray-50 rounded-lg p-8 text-center">
          <h2 className="mb-4">Get in Touch</h2>
          <address className="not-italic space-y-2 text-gray-600 mb-6">
            <p>{contact.addressDisplay}</p>
            <p>
              {contact.phones.map((phone, i) => (
                <React.Fragment key={phone}>
                  {i > 0 && ' / '}
                  <a href={`tel:${phone.replace(/[^\d+]/g, '')}`} className="hover:text-primary">{phone}</a>
                </React.Fragment>
              ))}
            </p>
            <p>
              <a href={`mailto:${contact.email}`} className="hover:text-primary">{contact.email}</a>
            </p>
          </address>
          <Button asChild>
            <a href="/contact">Send us a Message</a>
          </Button>
        </section>
      </div>

      <div className="mt-16">
        <PageFaq faqs={seo.faqs} />
      </div>

      {selectedMember && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm cursor-pointer"
          onClick={() => setSelectedMember(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={selectedMember.name}
            className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col h-full cursor-default"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
              <div>
                <h2 className="text-lg sm:text-xl font-semibold">{selectedMember.name}</h2>
                <p className="text-sm text-gray-500 mt-1">{selectedMember.role}</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Close"
                onClick={() => setSelectedMember(null)}
                className="h-8 w-8 rounded-full hover:bg-gray-100 cursor-pointer"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto space-y-4 mt-4 p-6 pr-7">
              {safeUrl(selectedMember.image) && (
                <div className="relative w-full overflow-hidden rounded-md flex items-center justify-center py-6">
                  <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-blue-100 flex-shrink-0">
                    <ImageWithFallback
                      src={safeUrl(selectedMember.image)}
                      alt={selectedMember.name}
                      className="w-full h-full object-cover"
                      fallbackSrc="/team/placeholder.jpg"
                    />
                  </div>
                </div>
              )}
              <div>
                <h3 className="text-md font-semibold mb-2">Professional Background</h3>
                <p className="text-gray-700 leading-relaxed text-sm whitespace-pre-line">{selectedMember.bio}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
